'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Zap, Check, AlertTriangle, ExternalLink, Loader2, Shield } from 'lucide-react';
import ConfirmModal from '@/components/ui/ConfirmModal';

const plans = [
  {
    id: 'plan-starter',
    name: 'Starter',
    price: 20,
    period: 'mo',
    description: 'Perfect for solo contractors just getting started',
    features: [
      '25 leads/month',
      'AI Leverage Scoring',
      'Photo-based estimates',
      '1 message template',
      'Email support',
    ],
    limitations: ['No bulk actions', 'No template customization'],
    color: 'border-[#2A2A2A]',
    badge: null,
  },
  {
    id: 'plan-pro',
    name: 'Pro',
    price: 29,
    period: 'mo',
    description: 'For growing service businesses closing $10k+ months',
    features: [
      'Unlimited leads',
      'AI Leverage Scoring',
      'Photo-based estimates',
      '3 message templates',
      'Bulk lead actions',
      'Priority support',
      'CSV export',
    ],
    limitations: [],
    color: 'border-blue-600/40',
    badge: 'Most Popular',
  },
  {
    id: 'plan-agency',
    name: 'Agency',
    price: 79,
    period: 'mo',
    description: 'Multiple service lines, team access, white-label',
    features: [
      'Unlimited leads',
      'Multiple service types',
      'Team member access (3 seats)',
      'White-label quote pages',
      'Custom domain',
      'API access',
      'Dedicated support',
    ],
    limitations: [],
    color: 'border-amber-500/30',
    badge: 'Best Value',
  },
];

interface BillingEvent {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'Paid' | 'Pending' | 'Failed';
}

const billingHistory: BillingEvent[] = [
  { id: 'inv-001', date: '2026-04-20', description: 'Pro Plan — Monthly', amount: 29, status: 'Paid' },
  { id: 'inv-002', date: '2026-03-20', description: 'Pro Plan — Monthly', amount: 29, status: 'Paid' },
  { id: 'inv-003', date: '2026-02-20', description: 'Pro Plan — Monthly', amount: 29, status: 'Paid' },
  { id: 'inv-004', date: '2026-01-20', description: 'Pro Plan — Monthly', amount: 29, status: 'Paid' },
];

export default function SubscriptionSettings() {
  const [currentPlan] = useState('plan-pro');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  const handleUpgrade = async (planId: string) => {
    setUpgrading(planId);
    // TODO: POST /api/stripe/create-checkout-session with { planId } — redirect to Stripe Checkout
    await new Promise((r) => setTimeout(r, 1200));
    setUpgrading(null);
    toast.success('Redirecting to Stripe checkout...');
  };

  const handleCancelSubscription = () => {
    // TODO: POST /api/stripe/cancel-subscription — cancel at period end via Stripe API
    setShowCancelModal(false);
    toast.success('Subscription cancelled. Access continues until May 20, 2026.');
  };

  const handleManageBilling = () => {
    // TODO: POST /api/stripe/create-portal-session — redirect to Stripe Customer Portal
    toast.success('Opening Stripe billing portal...');
  };

  return (
    <div className="space-y-6 max-w-screen-lg">
      {/* Current plan status */}
      <div className="leverage-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-600/25 flex items-center justify-center">
              <Zap size={22} className="text-blue-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-white font-800 text-lg">Pro Plan</span>
                <span className="bg-blue-600/20 text-blue-400 border border-blue-600/30 rounded-full px-2 py-0.5 text-[10px] font-700 uppercase">Active</span>
              </div>
              <p className="text-[#666] text-sm">$29/month · Renews May 20, 2026</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleManageBilling}
              className="leverage-btn-secondary text-sm py-2 px-4 flex items-center gap-1.5"
            >
              <CreditCard size={14} />
              Manage Billing
              <ExternalLink size={12} className="text-[#555]" />
            </button>
            <button
              onClick={() => setShowCancelModal(true)}
              className="text-red-400 hover:text-red-300 text-sm font-500 px-4 py-2 rounded-lg hover:bg-red-500/10 transition-colors"
            >
              Cancel Plan
            </button>
          </div>
        </div>

        {/* Usage */}
        <div className="mt-5 pt-4 border-t border-[#1F1F1F] grid grid-cols-3 gap-4">
          {[
            { label: 'Leads This Month', value: '47', limit: 'Unlimited', pct: null },
            { label: 'Templates Used', value: '3', limit: '3 max', pct: 100 },
            { label: 'Estimates Generated', value: '47', limit: 'Unlimited', pct: null },
          ].map(({ label, value, limit, pct }) => (
            <div key={`usage-${label}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[#555] text-xs">{label}</span>
                <span className="text-[#888] text-xs">{limit}</span>
              </div>
              <p className="text-white font-700 text-xl tabular-nums mb-2">{value}</p>
              {pct !== null && (
                <>
                  <div className="w-full bg-[#1A1A1A] rounded-full h-1">
                    <div
                      className={`h-1 rounded-full ${pct >= 90 ? 'bg-amber-500' : 'bg-blue-600'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {pct >= 90 && (
                    <p className="text-amber-400 text-[10px] mt-1 flex items-center gap-1">
                      <AlertTriangle size={10} />
                      At limit — upgrade to add more
                    </p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Plan comparison */}
      <div>
        <h3 className="text-white font-700 text-base mb-4">Change Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlan;
            return (
              <div
                key={plan.id}
                className={`relative leverage-card p-5 border-2 transition-all duration-150 ${plan.color} ${isCurrent ? 'ring-1 ring-blue-600/30' : ''}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-700 uppercase ${plan.id === 'plan-pro' ? 'bg-blue-600 text-white' : 'bg-amber-500 text-black'}`}>
                      {plan.badge}
                    </span>
                  </div>
                )}
                <div className="mb-4">
                  <h4 className="text-white font-800 text-lg">{plan.name}</h4>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-3xl font-800 text-white tabular-nums">${plan.price}</span>
                    <span className="text-[#555] text-sm">/{plan.period}</span>
                  </div>
                  <p className="text-[#666] text-xs mt-2 leading-relaxed">{plan.description}</p>
                </div>

                <ul className="space-y-2 mb-5">
                  {plan.features.map((feat) => (
                    <li key={`feat-${plan.id}-${feat}`} className="flex items-start gap-2">
                      <Check size={13} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-[#AAA] text-xs">{feat}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="w-full py-2.5 rounded-lg bg-blue-600/10 border border-blue-600/20 text-center text-blue-400 text-sm font-600">
                    Current Plan
                  </div>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={upgrading === plan.id}
                    className={`w-full py-2.5 rounded-lg text-sm font-600 transition-all duration-150 active:scale-95 flex items-center justify-center gap-2 ${
                      plan.id === 'plan-agency' ?'bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25' :'leverage-btn-primary'
                    }`}
                  >
                    {upgrading === plan.id ? (
                      <><Loader2 size={14} className="animate-spin" /> Processing...</>
                    ) : (
                      plan.price > 29 ? `Upgrade to ${plan.name}` : `Downgrade to ${plan.name}`
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing history */}
      <div className="leverage-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h3 className="text-white font-700 text-base">Billing History</h3>
          <button
            onClick={handleManageBilling}
            className="text-blue-400 text-xs font-600 hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            View in Stripe <ExternalLink size={11} />
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#111]">
              <th className="px-5 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Date</th>
              <th className="px-5 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Description</th>
              <th className="px-5 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Amount</th>
              <th className="px-5 py-3 text-left text-[11px] font-600 text-[#555] uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {billingHistory.map((event) => (
              <tr key={event.id} className="border-b border-[#0F0F0F] hover:bg-[#141414] transition-colors">
                <td className="px-5 py-3 text-[#888] text-xs tabular-nums">
                  {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="px-5 py-3 text-[#CCC] text-sm">{event.description}</td>
                <td className="px-5 py-3 text-white font-700 tabular-nums">${event.amount}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-600 ${
                    event.status === 'Paid' ? 'bg-emerald-500/10 text-emerald-400' :
                    event.status === 'Pending'? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {event.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Security note */}
      <div className="flex items-start gap-3 p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl">
        <Shield size={15} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-[#555] text-xs leading-relaxed">
          All billing is processed securely through Stripe. Serious Leverage never stores your card details. You can manage payment methods, download invoices, and update billing info directly in the Stripe portal.
        </p>
      </div>

      <ConfirmModal
        isOpen={showCancelModal}
        title="Cancel Your Subscription"
        description="Your Pro plan will remain active until May 20, 2026. After that, your account will be downgraded and you'll lose access to unlimited leads, bulk actions, and template customization."
        confirmLabel="Yes, Cancel Plan"
        onConfirm={handleCancelSubscription}
        onCancel={() => setShowCancelModal(false)}
        variant="danger"
      />
    </div>
  );
}